import { useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

interface BulkPostResponse {
  completedOrgIds: string[];
  failedOrgIds: string[];
  errorMessages: { [orgId: string]: string };
}

const useOrgMembersBulkPost = (orgIds: string[], roleID: string, users: string[]) => {
  const [inProgress, setInProgress] = useState(false);
  const [completedOrgIds, setCompletedOrgIds] = useState<string[]>([]);
  const [failedOrgIds, setFailedOrgIds] = useState<string[]>([]);
  const [currentOrgIndex, setCurrentOrgIndex] = useState<number | null>(null);
  const [errorMessages, setErrorMessages] = useState<{ [orgId: string]: string }>({});
  const { getAccessTokenSilently } = useAuth0();

  const trigger = useCallback(async (): Promise<BulkPostResponse> => {
    if (inProgress || orgIds.length === 0) {
      return { completedOrgIds: [], failedOrgIds: [], errorMessages: { ...errorMessages } };
    }

    setInProgress(true);
    setCompletedOrgIds([]);
    setFailedOrgIds([]);
    setCurrentOrgIndex(0);

    const postMembers = async (orgId: string) => {
      const url = `${process.env.REACT_APP_API_BASE_URL}/organizations/${orgId}/members`;
      const token = await getAccessTokenSilently();

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ roleID, users }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          `Failed to add members (code: ${response.status}, message: ${errorData?.error || response.statusText})`,
        );
      }
    };

    for (let i = 0; i < orgIds.length; i++) {
      setCurrentOrgIndex(i);
      try {
        await postMembers(orgIds[i]);
        setCompletedOrgIds(prev => [...prev, orgIds[i]]);
      } catch (error) {
        setFailedOrgIds(prev => [...prev, orgIds[i]]);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        setErrorMessages(prev => ({ ...prev, [orgIds[i]]: errorMessage }));
      }
    }

    setCurrentOrgIndex(null);
    setInProgress(false);

    return {
      completedOrgIds: orgIds.filter(id => !failedOrgIds.includes(id)),
      failedOrgIds: [...failedOrgIds],
      errorMessages: { ...errorMessages },
    };
  }, [orgIds, inProgress, errorMessages, failedOrgIds, getAccessTokenSilently, roleID, users]);

  return {
    trigger,
    inProgress,
    completedOrgIds,
    failedOrgIds,
    currentOrgId: currentOrgIndex !== null ? orgIds[currentOrgIndex] : null,
    errorMessages,
  };
};

export default useOrgMembersBulkPost;
